import React, {FC, useCallback, useEffect, useRef, useState} from 'react';

export interface IChartSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
}

const PAD = {top: 12, right: 16, bottom: 24, left: 40};

const niceMax = (value: number): number => {
  if (value <= 4) return 4;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const max = Math.ceil(value / magnitude) * magnitude;
  // Whole-number midline
  return max % 2 === 0 ? max : max + magnitude;
};

export const LineChart: FC<{labels: string[]; series: IChartSeries[]; height?: number}> = ({labels, series, height = 200}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => setWidth(entries[0]!.contentRect.width));
    observer.observe(el);
    setWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  const plotW = Math.max(width - PAD.left - PAD.right, 10);
  const plotH = height - PAD.top - PAD.bottom;
  const max = niceMax(Math.max(...series.flatMap((s) => s.values), 1));
  const stepX = labels.length > 1 ? plotW / (labels.length - 1) : 0;

  const x = (i: number) => PAD.left + i * stepX;
  const y = (v: number) => PAD.top + plotH - (v / max) * plotH;

  const handleMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const bounds = e.currentTarget.getBoundingClientRect();
      const offset = e.clientX - bounds.left;
      setHover(Math.max(0, Math.min(labels.length - 1, Math.round(offset / (stepX || 1)))));
    },
    [labels.length, stepX],
  );

  const ticks = [0, max / 2, max];

  return (
    <div>
      {series.length > 1 && (
        <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1">
          {series.map((s) => (
            <div key={s.key} className="flex items-center gap-1.5 text-[12px] text-gray-500">
              <span className="inline-block h-2 w-2 rounded-full" style={{backgroundColor: s.color}} />
              {s.label}
            </div>
          ))}
        </div>
      )}

      <div ref={ref} className="relative w-full">
        {width > 0 && (
          <svg width={width} height={height} role="img" aria-label={`Daily volume for ${series.map((s) => s.label).join(' and ')}`}>
            {ticks.map((t) => (
              <g key={t}>
                <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} stroke="#eef0f3" strokeWidth={1} />
                <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" className="tabular-nums" fontSize={11} fill="#9ca3af">
                  {Math.round(t)}
                </text>
              </g>
            ))}

            {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + plotH} stroke="#d6d8de" strokeWidth={1} />}

            {series.map((s) => (
              <polyline
                key={s.key}
                fill="none"
                stroke={s.color}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
              />
            ))}

            {series.map((s) => {
              const last = s.values.length - 1;
              return (
                <circle key={`${s.key}-end`} cx={x(last)} cy={y(s.values[last] ?? 0)} r={4} fill={s.color} stroke="#fff" strokeWidth={2} />
              );
            })}

            {hover !== null &&
              series.map((s) => (
                <circle
                  key={`${s.key}-hover`}
                  cx={x(hover)}
                  cy={y(s.values[hover] ?? 0)}
                  r={4}
                  fill={s.color}
                  stroke="#fff"
                  strokeWidth={2}
                />
              ))}

            {labels.map((label, i) =>
              // Thin labels on narrow charts
              i % Math.ceil(labels.length / 7) === 0 ? (
                <text key={label} x={x(i)} y={height - 6} textAnchor="middle" className="tabular-nums" fontSize={11} fill="#9ca3af">
                  {label}
                </text>
              ) : null,
            )}

            <rect
              x={PAD.left - stepX / 2}
              y={PAD.top}
              width={plotW + stepX}
              height={plotH}
              fill="transparent"
              onMouseMove={handleMove}
              onMouseLeave={() => setHover(null)}
            />
          </svg>
        )}

        {hover !== null && width > 0 && (
          <div
            className="pointer-events-none absolute z-10 rounded-md border border-[#ebecf0] bg-white px-2.5 py-1.5 shadow-sm"
            style={{left: Math.min(Math.max(x(hover) - 60, 0), Math.max(width - 130, 0)), top: 0}}>
            <div className="text-[11px] font-medium text-[#33364b]">{labels[hover]}</div>
            {series.map((s) => (
              <div key={s.key} className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-500">
                <span className="inline-block h-2 w-2 rounded-full" style={{backgroundColor: s.color}} />
                {s.label}
                <span className="tabular-nums font-medium text-[#33364b]">{s.values[hover]}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
